/** @jsxImportSource @emotion/react */

import { vars } from "@imwebme/clay-token";
import type { ComponentPropsWithRef, ElementType } from "react";
import { forwardRef } from "react";
import { Flex } from "../utilities";
import { cssVariableMap, inputContainerCSS } from "./Textfield.css";
import { TextfieldInput } from "./Textfield.input";
import type { TextfieldBoxProps, TextfieldInputProps } from "./Textfield.types";

type TextfieldBoxComponents = <E extends ElementType>(
  props: TextfieldBoxProps<E> & {
    ref?: ComponentPropsWithRef<E>["ref"];
  },
) => JSX.Element | null;

export const TextfieldBox: TextfieldBoxComponents = forwardRef(function TextfieldBox<
  E extends ElementType,
>(
  {
    variant = "outlined",
    children,
    width = "full",
    size = "medium",
    className,
    sx,
    as,
    native,
  }: TextfieldBoxProps<E>,
  ref: ComponentPropsWithRef<ElementType>["ref"],
) {
  return (
    <Flex
      as={as || "div"}
      ref={ref}
      display={width === "full" ? "flex" : "inline-flex"}
      width={width === "full" ? "100%" : width}
      className={className}
      alignItems="center"
      minWidth="80px"
      native={{
        "data-clay-textfield-container-size": size,
        ...native,
        style: {
          ...cssVariableMap.variant[variant],
          ...cssVariableMap.size[size],
          ...native?.style,
        },
      }}
      css={[
        inputContainerCSS,
        sx,
        {
          "&:has(input:disabled)": {
            "& :has(svg) svg": {
              color: vars.semantic.color.iconDisabled,
            },
          },
        },
      ]}
    >
      {children}
    </Flex>
  );
});

export const TextfieldNoAddon = ({
  variant,
  size,
  width,
  ...restProps
}: TextfieldBoxProps<"div"> & Omit<TextfieldInputProps, "size">) => (
  <TextfieldBox variant={variant} size={size} width={width}>
    <TextfieldInput {...restProps} />
  </TextfieldBox>
);
